import { useCallback, useMemo, useState } from "react"

import { useMatch, useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { AddIcon } from "@l2r-front/l2r-icons"
import { useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { MoreButton, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { NavigationOrigins, useNavigateWithSearchParams, useUrlParams } from "@l2r-front/l2r-utils"

import { DocumentEditModal } from "../../components/DocumentEditModal"
import { FILTERS } from "../../constants/filters"
import { I18N_NAMESPACE } from "../../constants/i18n"
import { APP_DOCUMENT, MODULE_DOCUMENTS, NETWORK_ATTACHMENT, ROAD_ATTACHMENT, SECTION_ATTACHMENT } from "../../constants/modules"
import { LINEAR_LOCATION_END, LINEAR_LOCATION_START, TYPE_ID } from "../../constants/urlParams"
import { DocumentAddForm } from "../../forms/DocumentAddForm"

import { DocumentAttachedObjectSidebarContent } from "../DocumentAttachedObjectSidebarContent"

import * as Styled from "./DocumentAttachedObjectSidebar.styled"

export function DocumentAttachedObjectSidebar() {

    const { t } = useTranslation([I18N_NAMESPACE])
    const navigate = useNavigateWithSearchParams()
    const { road } = useParams()
    const [documentAddModal, setDocumentAddModal] = useState(false)
    const readOnly = useRoleIsReadOnly()
    const matchSection = useMatch(`/:networkSlug/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/:road/attached/${SECTION_ATTACHMENT}`)
    const match = useMatch(`/:networkSlug/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/:road/attached/:attachmentType/*`)
    const matchNetwork = useMatch(`/:networkSlug/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/attached/${NETWORK_ATTACHMENT}`)
    const attachmentType = matchNetwork ? NETWORK_ATTACHMENT : matchSection ? SECTION_ATTACHMENT : match ? match.params.attachmentType : null
    const { attachmentId } = useParams()
    const { getParam } = useUrlParams()
    const isMobile = useIsMobileDevice()

    const navigateBack = useCallback(() => {
        navigate(`../${MODULE_DOCUMENTS}/${road || ""}`, {
            paramsToDelete: [LINEAR_LOCATION_END, LINEAR_LOCATION_START, TYPE_ID],
            navigationOrigin: NavigationOrigins.ROAD_FROM_BACK_DETAIL,
        })
    }, [navigate, road])

    const handleAddDocumentMenuSelect = useCallback(() => {
        setDocumentAddModal(true)
    }, [])

    const linearLocation = useMemo(() => {
        if (attachmentType === SECTION_ATTACHMENT) {
            return ({
                end: getParam(LINEAR_LOCATION_END),
                road_type: SECTION_ATTACHMENT.toUpperCase(),
                road,
                start: getParam(LINEAR_LOCATION_START),
                typeid: getParam(TYPE_ID),
            })

        } else if (attachmentType === ROAD_ATTACHMENT) {
            return ({
                road,
                road_type: ROAD_ATTACHMENT.toUpperCase(),
                typeid: getParam(TYPE_ID),
            })
        }
    }, [attachmentType, getParam, road])

    return (
        <Styled.BottomSheetContent
            id="documentDetails-sidebar"
            title={t(I18N_NAMESPACE, "containers.documentAttachedObjectSidebar.title")}
            onBack={navigateBack}
            titleBottomComponent={!isMobile && <Styled.DocumentFilterList enabledFilters={FILTERS} disableDelete />}
            titleSideComponent={!readOnly && <MoreButton
                color="primary"
                icon={AddIcon}
                size={"small"}
                onClick={handleAddDocumentMenuSelect} />
            }
        >
            <DocumentAttachedObjectSidebarContent />
            {
                documentAddModal && <DocumentEditModal
                    attachedObjectId={attachmentId}
                    attachedObjectType={attachmentType}
                    FormComponent={DocumentAddForm}
                    onClose={() => setDocumentAddModal(false)}
                    open={!!documentAddModal}
                    selectionType={attachmentType}
                    documentLocation={linearLocation}
                />
            }
        </Styled.BottomSheetContent>
    )
}
